/**
 * ============================================================
 * Wingman RC5 — Indoor Airport Intelligence
 * AirportDiscoveryService.ts
 *
 * Provider-neutral indexed search engine over airport POIs.
 * Supports free-text search, category filtering, and
 * level-based retrieval.
 *
 * IAM-M16B-R1.4B — Airport Discovery + Search Engine
 * ============================================================
 */

import {
  type AirportPOI,
  type AirportSearchRequest,
  type AirportSearchResponse,
  type AirportSearchResult,
  type IAirportDiscoveryService,
  AirportCategory,
} from './AirportDiscoveryContracts';

// ── Scoring Helpers ──────────────────────────────────────────

function computeSearchScore(poi: AirportPOI, request: AirportSearchRequest): { score: number; reason: string } {
  let score = 0;
  let reason = '';

  const query = (request.query ?? '').toLowerCase().trim();

  if (query) {
    const nameLower = poi.name.toLowerCase();

    // Exact match
    if (nameLower === query) {
      score += 1.0;
      reason = 'exact name match';
    }
    // Prefix strip match (e.g., "Gate B20" matches POI "B20", "Gate B32" matches POI "B32")
    else if (query.replace(/^(gate|concourse|terminal|checkpoint)\s+/i, '').trim() === nameLower) {
      score += 0.95;
      reason = 'name matches after prefix strip';
    }
    // Starts-with match
    else if (nameLower.startsWith(query)) {
      score += 0.8;
      reason = 'name starts with query';
    }
    // Contains match
    else if (nameLower.includes(query)) {
      score += 0.6;
      reason = 'name contains query';
    }
    // Query contains name match (e.g. query is "Gate B20" and name is "B20")
    else if (query.includes(nameLower) && nameLower.length >= 2) {
      score += 0.85;
      reason = 'query contains name';
    }
    // Word-boundary match (e.g., "B20" matches "Gate B20")
    else {
      const words = nameLower.split(/\s+/);
      const queryWords = query.split(/\s+/);
      const matchedWords = queryWords.filter(qw => words.some(w => w.includes(qw)));
      if (matchedWords.length > 0) {
        score += 0.4 * (matchedWords.length / queryWords.length);
        reason = `partial word match (${matchedWords.length}/${queryWords.length})`;
      }
    }
  } else {
    // No query — base score for filter-only matches
    score = 0.5;
    reason = 'filter match';
  }

  // Category bonus
  if (request.category && poi.category === request.category) {
    score += 0.2;
    reason += reason ? ' + category match' : 'category match';
  }

  // Level bonus
  if (request.level !== undefined && poi.level === request.level) {
    score += 0.1;
    reason += reason ? ' + level match' : 'level match';
  }

  return { score: Math.min(score, 1.0), reason: reason || 'no match' };
}

// ── Service Implementation ───────────────────────────────────

export class AirportDiscoveryService implements IAirportDiscoveryService {
  private readonly poiIndex = new Map<string, AirportPOI>();
  private readonly categoryIndex = new Map<AirportCategory, AirportPOI[]>();

  get count(): number {
    return this.poiIndex.size;
  }

  /**
   * Index a batch of POIs for searching.
   * Appends to existing index (does not clear).
   */
  index(pois: readonly AirportPOI[]): void {
    for (const poi of pois) {
      this.poiIndex.set(poi.id, poi);

      if (!this.categoryIndex.has(poi.category)) {
        this.categoryIndex.set(poi.category, []);
      }
      this.categoryIndex.get(poi.category)!.push(poi);
    }
  }

  /**
   * Search indexed POIs by query, category, level, and/or terminal.
   */
  search(request: AirportSearchRequest): AirportSearchResponse {
    const limit = request.limit ?? 20;
    const candidates: AirportSearchResult[] = [];

    for (const poi of this.poiIndex.values()) {
      // Apply hard filters
      if (request.category && poi.category !== request.category) continue;
      if (request.level !== undefined && poi.level !== request.level) continue;
      if (request.terminal && poi.terminal !== request.terminal) continue;

      const { score, reason } = computeSearchScore(poi, request);

      // Only include results with positive score
      if (score > 0) {
        candidates.push({ poi, score, matchReason: reason });
      }
    }

    // Sort by score descending, then by name for stability
    candidates.sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;
      return a.poi.name.localeCompare(b.poi.name);
    });

    const results = candidates.slice(0, limit);

    return {
      results,
      totalMatched: candidates.length,
      query: request,
      timestamp: Date.now(),
    };
  }

  /**
   * Get a POI by its stable ID.
   */
  getById(id: string): AirportPOI | undefined {
    return this.poiIndex.get(id);
  }

  /**
   * Get all POIs in a category.
   */
  getByCategory(category: AirportCategory): readonly AirportPOI[] {
    return this.categoryIndex.get(category) ?? [];
  }
}
